/**
 * Donations page: Пожертвования — поддержка служения церкви.
 * RUB requisites from API (same as tickets), THB from config + QR.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import {
  IconCopy,
  IconCheck,
  IconExternalLink,
  IconDownload,
} from "../../components/Icons.tsx";
import { graphql, getErrorMessage } from "../../lib/graphql.ts";
import {
  PAYMENT_REQUISITES_QUERY,
  type PaymentRequisites,
} from "../../lib/graphql.ts";
import { getTelegram, haptic } from "../../lib/telegram.ts";

const BODY_TEXT = `Поддержите служение церкви

Церковь строится не только кирпичами, но верой, жертвенной любовью и щедростью.

Ваше пожертвование — это вклад в спасённые жизни, растущие семьи и служение, которое несёт свет на земле Таиланда. Становясь нашими финансовыми партнерами, вы входите как участники того труда, который мы совершаем на этой земле Таиланда.

Поддерживая финансами церковь, вы участвуете в том, что имеет вечную ценность.

🙏 Спасибо за ваше открытое сердце и веру в дело Божье!`;

// Реквизиты в батах для пожертвований (отдельно от билетов)
const THB_REQUISITES = {
  bank: "KRUNGSRI BANK",
  account: "4751-2158-14",
};

const DONATION_QR_THB_URL = "/content/donation-qr-thb.png";

const CARD_CLASS =
  "rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50";

export function DonationsPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const [rubRequisites, setRubRequisites] = useState<PaymentRequisites | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showQrOverlay, setShowQrOverlay] = useState(false);

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      haptic("success");
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopiedField(field);
      haptic("success");
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const data = await graphql<{ paymentRequisites: PaymentRequisites[] }>(
          PAYMENT_REQUISITES_QUERY
        );
        const rub = data.paymentRequisites.find((r) => r.currency === "RUB");
        setRubRequisites(rub ?? null);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (showQrOverlay) {
        setShowQrOverlay(false);
      } else if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, showQrOverlay]);

  const handleOpenQr = () => {
    haptic("light");
    setShowQrOverlay(true);
  };

  const handleCloseQrOverlay = () => {
    haptic("light");
    setShowQrOverlay(false);
  };

  const handleDownloadQr = async () => {
    haptic("light");
    try {
      const res = await fetch(DONATION_QR_THB_URL);
      if (!res.ok) throw new Error("Не удалось загрузить изображение");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "donation-qr-thb.png";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      haptic("success");
    } catch {
      if (tg?.showAlert) tg.showAlert("Не удалось скачать изображение");
    }
  };

  // Full-screen QR overlay (центр по экрану, кнопка назад)
  if (showQrOverlay) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black flex flex-col"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div
          className="absolute top-0 left-0 right-0 z-20"
          style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
        >
          <div className="bg-gradient-to-b from-black/70 to-transparent">
            <div className="flex items-center justify-between px-4 py-3">
              <button
                type="button"
                onClick={handleCloseQrOverlay}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white active:bg-white/20"
                aria-label="Назад"
              >
                <svg
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path d="M19 12H5M12 19L5 12L12 5" />
                </svg>
              </button>
              <span className="text-white/90 text-[14px] font-medium">
                QR для пожертвования в батах
              </span>
              <div className="w-10" />
            </div>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center min-h-0 p-4">
          <img
            src={DONATION_QR_THB_URL}
            alt="QR для пожертвования в батах"
            className="max-w-full max-h-full object-contain"
          />
        </div>
        <div
          className="pb-4"
          style={{
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
          }}
        >
          <div className="flex justify-center gap-3 px-4">
            <button
              type="button"
              onClick={handleCloseQrOverlay}
              className="flex-1 max-w-[200px] py-2.5 rounded-xl font-medium bg-white/10 text-white active:bg-white/20"
            >
              Закрыть
            </button>
            <button
              type="button"
              onClick={() => {
                handleDownloadQr();
                handleCloseQrOverlay();
              }}
              className="flex-1 max-w-[200px] py-2.5 rounded-xl font-medium bg-[var(--color-accent)] text-white active:opacity-90"
            >
              Скачать
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !isLoading) {
    return (
      <Layout>
        <div className="rounded-2xl overflow-hidden bg-amber-50/95 shadow-sm border border-amber-200/60 p-4">
          <p className="text-slate-700 text-sm">{error}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <h1 className="text-xl font-bold text-slate-800 mb-4">Пожертвования</h1>

      {/* Hero image */}
      <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100">
        <img
          src="/content/donations.png"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm p-4 xs:p-5 mb-6 border border-slate-100">
        <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap prose prose-slate prose-sm max-w-none">
          {BODY_TEXT}
        </p>
      </div>

      {/* Реквизиты в рублях (как в оплате билетов) */}
      {rubRequisites && (
        <div className="mb-6">
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">
            Реквизиты в рублях (₽)
          </p>
          <p className="text-[11px] xs:text-xs text-slate-400 mb-3">
            Нажмите на строку, чтобы скопировать
          </p>
          <div className={CARD_CLASS}>
            <div className="divide-y divide-slate-100">
              <button
                type="button"
                onClick={() => copyToClipboard(rubRequisites.bank, "rub-bank")}
                className="w-full text-left px-4 xs:px-5 py-3 min-h-[48px] hover:bg-slate-50/80 active:bg-slate-100 transition-colors relative flex justify-between items-center gap-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] xs:text-xs text-slate-400 uppercase tracking-wide mb-0.5">
                    Банк
                  </p>
                  <p className="font-medium text-[15px] text-slate-900">
                    {rubRequisites.bank}
                  </p>
                </div>
                <span className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                  {copiedField === "rub-bank" ? (
                    <IconCheck className="w-4 h-4 text-green-500" />
                  ) : (
                    <IconCopy className="w-4 h-4" />
                  )}
                </span>
              </button>
              <button
                type="button"
                onClick={() =>
                  copyToClipboard(rubRequisites.account, "rub-account")
                }
                className="w-full text-left px-4 xs:px-5 py-3 min-h-[48px] hover:bg-slate-50/80 active:bg-slate-100 transition-colors flex justify-between items-center gap-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] xs:text-xs text-slate-400 uppercase tracking-wide mb-0.5">
                    Счёт
                  </p>
                  <p className="font-medium text-[15px] text-slate-900 font-mono">
                    {rubRequisites.account}
                  </p>
                </div>
                <span className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                  {copiedField === "rub-account" ? (
                    <IconCheck className="w-4 h-4 text-green-500" />
                  ) : (
                    <IconCopy className="w-4 h-4" />
                  )}
                </span>
              </button>
              <button
                type="button"
                onClick={() => copyToClipboard(rubRequisites.name, "rub-name")}
                className="w-full text-left px-4 xs:px-5 py-3 min-h-[48px] hover:bg-slate-50/80 active:bg-slate-100 transition-colors flex justify-between items-center gap-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] xs:text-xs text-slate-400 uppercase tracking-wide mb-0.5">
                    Получатель
                  </p>
                  <p className="font-medium text-[15px] text-slate-900">
                    {rubRequisites.name}
                  </p>
                </div>
                <span className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                  {copiedField === "rub-name" ? (
                    <IconCheck className="w-4 h-4 text-green-500" />
                  ) : (
                    <IconCopy className="w-4 h-4" />
                  )}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Реквизиты в батах + QR */}
      <div className="mb-6">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">
          Реквизиты в батах (฿)
        </p>
        <p className="text-[11px] xs:text-xs text-slate-400 mb-3">
          Нажмите на строку, чтобы скопировать
        </p>
        <div className={CARD_CLASS}>
          <div className="divide-y divide-slate-100">
            <button
              type="button"
              onClick={() => copyToClipboard(THB_REQUISITES.bank, "thb-bank")}
              className="w-full text-left px-4 xs:px-5 py-3 min-h-[48px] hover:bg-slate-50/80 active:bg-slate-100 transition-colors flex justify-between items-center gap-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[10px] xs:text-xs text-slate-400 uppercase tracking-wide mb-0.5">
                  Банк
                </p>
                <p className="font-medium text-[15px] text-slate-900">
                  {THB_REQUISITES.bank}
                </p>
              </div>
              <span className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                {copiedField === "thb-bank" ? (
                  <IconCheck className="w-4 h-4 text-green-500" />
                ) : (
                  <IconCopy className="w-4 h-4" />
                )}
              </span>
            </button>
            <button
              type="button"
              onClick={() =>
                copyToClipboard(THB_REQUISITES.account, "thb-account")
              }
              className="w-full text-left px-4 xs:px-5 py-3 min-h-[48px] hover:bg-slate-50/80 active:bg-slate-100 transition-colors flex justify-between items-center gap-3"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[10px] xs:text-xs text-slate-400 uppercase tracking-wide mb-0.5">
                  Счёт
                </p>
                <p className="font-medium text-[15px] text-slate-900 font-mono">
                  {THB_REQUISITES.account}
                </p>
              </div>
              <span className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center bg-slate-100 text-slate-500">
                {copiedField === "thb-account" ? (
                  <IconCheck className="w-4 h-4 text-green-500" />
                ) : (
                  <IconCopy className="w-4 h-4" />
                )}
              </span>
            </button>
          </div>

          {/* QR для Thai Baht — открыть / скачать */}
          <div className="p-4 xs:p-5 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-700 mb-3">
              QR-код для перевода в батах
            </p>
            <div className="flex items-center gap-4">
              <img
                src={DONATION_QR_THB_URL}
                alt="QR для пожертвования в батах"
                className="w-32 h-32 rounded-xl border border-slate-200 object-contain bg-white"
              />
              <div className="flex flex-col gap-2 flex-1">
                <button
                  type="button"
                  onClick={handleOpenQr}
                  className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-slate-100 text-slate-700 font-medium text-sm active:bg-slate-200 transition-colors"
                >
                  <IconExternalLink className="w-4 h-4" />
                  Открыть
                </button>
                <button
                  type="button"
                  onClick={handleDownloadQr}
                  className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-[var(--color-accent)] text-white font-medium text-sm active:opacity-90 transition-colors"
                >
                  <IconDownload className="w-4 h-4" />
                  Скачать
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="h-4" />
    </Layout>
  );
}
